import { Celebration, Piggington, Icon } from "../components/Common.jsx";
import { track } from "../lib/metrics.js";
import { NumericKeypad } from "../components/Tests.jsx";
import { effectiveAge } from "../lib/age.js";
import { withAnswerPrompt } from "../lib/maths.js";
import { equippedCharacterBaseId, pigDisplayNameFor, wornPiggingtonCosmetics } from "../lib/oinks.js";
import {
  BOSS_CLEAR_BONUS_POINTS,
  CROW_DROP_MS,
  CROW_HAT_KIND,
  CROW_TELEGRAPH_MS,
  CROW_WAVE_MS,
  GENTLE_HITBOX_FACTOR,
  HINT_KEY,
  OBJECT_KINDS,
  SC_BOSS_TEST,
  STARTING_HEARTS,
  WARMUP_TARGET,
  applyCatch,
  applyMissedGood,
  arcadeEnabledFor,
  arcadeWarmupModeFor,
  bestKeyFor,
  bossWaveCleared,
  crowDropTableFor,
  crowPatrolSpeed,
  crowPosAt,
  initialRunState,
  isBossStage,
  modeForAge,
  pickCrowDrop,
  pickSpawnKind,
  rottenPenalty,
  scTestWarmupQuestion,
  stageForScore,
  stageParams,
  warmupKeyFor,
  warmupQuestion,
} from "../lib/snackCatch.js";
import { todayStr } from "../lib/utils.js";
const { useState, useEffect, useCallback, useMemo, useRef } = React;

/* --------------------------- Piggington's Arcade — Snack Attack (R5) --------------------------- */
//
// The app's first pure-fun corner: a 60–120s catch-the-falling-food arcade loop starring
// Piggington in the child's equipped cosmetics, gated (by default) behind the daily Brain
// Warm-Up — 3 correct maths questions, generated fully offline by lib/snackCatch.js.
//
// Law 1 posture, enforced structurally: nothing in this file imports addTransaction, Oinks,
// OP, or any earning/request path. Score is a plain numeral labelled "Score"; no currency
// imagery appears on any game screen. The game writes NOTHING to household state — its only
// persistence is two device-local localStorage keys (personal best, warm-up date). That
// zero-write rule is what makes it genuinely offline-safe rather than "offline until it
// tries to save" (scope §1), so keep it absolute in any future change here.
//
// Rendering: no canvas, no libraries. React renders the chrome and the SET of live falling
// objects; a single requestAnimationFrame loop advances positions by writing
// style.transform directly on refs — React state is touched only on discrete events (spawn,
// catch, heart change, stage change), never per frame. ≤ ~12 concurrent objects keeps this
// comfortably 60fps on low-end tablets (scope §6.2).

/* ------------------------------ helpers ------------------------------ */

export function readBest(childId) {
  try {
    const raw = localStorage.getItem(bestKeyFor(childId));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed?.best === "number" ? parsed : null;
  } catch {
    return null;
  }
}

export function warmupDoneToday(childId) {
  try {
    return localStorage.getItem(warmupKeyFor(childId)) === todayStr();
  } catch {
    return false;
  }
}

/* ------------------------------ object sprites ------------------------------ */

// The seven falling-object symbols, inlined verbatim from the approved
// art/snack-attack-objects.svg (mockup-approved 2026-08-17) rather than shipped as a second
// external sprite file: they're only ever needed inside this overlay, inlining keeps the
// fragment-only <use href="#sa-…"> references trivially Safari-safe, and — like everything
// else in bundle.js — they're automatically offline with no service-worker cache-list edit.
// Same flat-fill navy-outline style as the character handoff; all symbols share a 60×60
// space. Visual grammar does the teaching: everything edible is warm and round; the two
// heart-losing hazards are the only cold/brown angular shapes; the rotten apple is
// deliberately a WRONG apple (same silhouette, sick colours, one fly).
function SnackSprites() {
  return (
    <svg width="0" height="0" style={{ display: "none" }} aria-hidden="true">
      <symbol id="sa-apple" viewBox="0 0 60 60">
        <circle cx="30" cy="34" r="19" fill="#ef4444" stroke="#1b2a4a" strokeWidth="3.5" />
        <path d="M30 16 q -1 -7 4 -10" fill="none" stroke="#1b2a4a" strokeWidth="3.5" strokeLinecap="round" />
        <ellipse cx="39" cy="10" rx="7" ry="4.5" fill="#22c55e" stroke="#1b2a4a" strokeWidth="3" transform="rotate(-20 39 10)" />
        <ellipse cx="23" cy="28" rx="4" ry="6" fill="#fca5a5" transform="rotate(-18 23 28)" />
      </symbol>
      <symbol id="sa-carrot" viewBox="0 0 60 60">
        <path d="M24 18 L36 18 L31 54 Q30 57 29 54 Z" fill="#f97316" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M26 5 L28 17 M30 3 L30 17 M34 5 L32 17" stroke="#22c55e" strokeWidth="3.5" strokeLinecap="round" />
        <path d="M26 27 h7 M27 36 h5" stroke="#1b2a4a" strokeWidth="2" strokeLinecap="round" />
      </symbol>
      <symbol id="sa-cupcake" viewBox="0 0 60 60">
        <path d="M17 32 L21 52 Q21 55 24 55 L36 55 Q39 55 39 52 L43 32 Z" fill="#fbbf24" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M23 34 L25 53 M30 34 L30 53 M37 34 L35 53" stroke="#1b2a4a" strokeWidth="1.6" />
        <path d="M15 32 Q14 20 24 20 Q25 12 33 13 Q42 13 43 21 Q47 24 45 32 Z" fill="#F5A3A8" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <circle cx="30" cy="9" r="4.5" fill="#e11d48" stroke="#1b2a4a" strokeWidth="3" />
      </symbol>
      <symbol id="sa-acorn" viewBox="0 0 60 60">
        <path d="M20 26 Q20 52 30 55 Q40 52 40 26 Z" fill="#fbbf24" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M17 26 Q17 14 30 14 Q43 14 43 26 Q37 29 30 29 Q23 29 17 26 Z" fill="#d97706" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M23 19 l3 5 M30 17 v6 M37 19 l-3 5" stroke="#1b2a4a" strokeWidth="1.6" strokeLinecap="round" />
        <path d="M30 14 q 0 -6 5 -7" fill="none" stroke="#1b2a4a" strokeWidth="3" strokeLinecap="round" />
        <path d="M50 12 l1.8 4 4 1.8 -4 1.8 -1.8 4 -1.8 -4 -4 -1.8 4 -1.8 Z" fill="#fde047" stroke="#b45309" strokeWidth="1.5" />
      </symbol>
      <symbol id="sa-rotten" viewBox="0 0 60 60">
        <circle cx="28" cy="36" r="17" fill="#a3a380" stroke="#1b2a4a" strokeWidth="3.5" />
        <path d="M28 20 q -1 -6 4 -9" fill="none" stroke="#1b2a4a" strokeWidth="3.5" strokeLinecap="round" />
        <circle cx="22" cy="32" r="4" fill="#6b6b47" />
        <circle cx="33" cy="42" r="3" fill="#6b6b47" />
        <circle cx="48" cy="16" r="2.4" fill="#1b2a4a" />
        <ellipse cx="45" cy="14" rx="2.6" ry="1.4" fill="#94a3b8" transform="rotate(-30 45 14)" />
        <ellipse cx="51" cy="14" rx="2.6" ry="1.4" fill="#94a3b8" transform="rotate(30 51 14)" />
      </symbol>
      <symbol id="sa-boot" viewBox="0 0 60 60">
        <path d="M22 8 L36 8 L36 34 L48 40 Q52 42 52 46 L52 50 L22 50 Z" fill="#92400e" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M25 15 h8 M25 21 h8 M25 27 h8" stroke="#fbbf24" strokeWidth="2.4" strokeLinecap="round" />
        <path d="M22 44 h30" stroke="#1b2a4a" strokeWidth="2.4" />
      </symbol>
      <symbol id="sa-can" viewBox="0 0 60 60">
        <path d="M19 14 L41 14 L39 52 Q39 55 36 55 L24 55 Q21 55 21 52 Z" fill="#cbd5e1" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <ellipse cx="30" cy="14" rx="11" ry="4" fill="#94a3b8" stroke="#1b2a4a" strokeWidth="3" />
        <path d="M20.5 28 h19 M21 40 h18" stroke="#1b2a4a" strokeWidth="2" />
        <rect x="22.5" y="30" width="15" height="8" rx="2" fill="#f1f5f9" stroke="#1b2a4a" strokeWidth="1.6" />
      </symbol>
      <symbol id="sa-tinhat" viewBox="0 0 60 60">
        <path d="M10 40 Q10 14 30 14 Q50 14 50 40 Z" fill="#8a9a5b" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <ellipse cx="30" cy="40" rx="25" ry="6.5" fill="#6b7b45" stroke="#1b2a4a" strokeWidth="3" />
        <circle cx="30" cy="19" r="3.2" fill="#576134" stroke="#1b2a4a" strokeWidth="2" />
        <path d="M16 28 Q30 23 44 28" fill="none" stroke="#576134" strokeWidth="2.4" strokeLinecap="round" opacity="0.7" />
      </symbol>
      <symbol id="sa-bandage" viewBox="0 0 60 60">
        <rect x="9" y="21" width="42" height="18" rx="9" fill="#fde3d0" stroke="#1b2a4a" strokeWidth="3.5" transform="rotate(-20 30 30)" />
        <rect x="26" y="23" width="4.5" height="14" fill="#e11d48" transform="rotate(-20 30 30)" />
        <rect x="19" y="27.75" width="17.5" height="4.5" fill="#e11d48" transform="rotate(-20 30 30)" />
        <circle cx="15" cy="25" r="1.8" fill="#c7a98c" transform="rotate(-20 30 30)" />
        <circle cx="45" cy="35" r="1.8" fill="#c7a98c" transform="rotate(-20 30 30)" />
      </symbol>
      {/* Crow Wave drops (GREEDY_CROW_SCOPE.md §0.8) — inlined verbatim from the handoff's
          snack-attack-drop-objects.svg, same 60×60/48px spec as everything above. Per scope
          §0.1 the handoff's sa-coin/sa-money-bag are NOT here (currency imagery never enters
          the arcade), and sa-anvil/sa-spike-ball/sa-gem/sa-safe stay out of the drop tables
          so they aren't shipped either — add them here if a future wave adopts them. */}
      <symbol id="sa-anvil-silver" viewBox="0 0 60 60">
        <path d="M8 20h44l-6 12H14z" fill="#8A8FA3" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M22 32h16l3 12H19z" fill="#8A8FA3" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <rect x="12" y="44" width="36" height="9" rx="3" fill="#8A8FA3" stroke="#1b2a4a" strokeWidth="3.5" />
        <path d="M14 23h10" fill="none" stroke="#1b2a4a" strokeWidth="1.8" opacity="0.5" />
      </symbol>
      <symbol id="sa-welly" viewBox="0 0 60 60">
        <path d="M20 6h14v24l14 8v14a4 4 0 0 1-4 4H12a4 4 0 0 1-4-4V34c0-6 4-8 8-10z" fill="#22c55e" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M8 46h40" fill="none" stroke="#1b2a4a" strokeWidth="2.4" />
        <path d="M20 6h14v6H20z" fill="none" stroke="#1b2a4a" strokeWidth="1.8" />
      </symbol>
      <symbol id="sa-sock" viewBox="0 0 60 60">
        <path d="M22 6h14v20c8 2 14 10 14 18 0 6-5 10-11 10H20c-6 0-11-4-11-10V16c0-6 5-10 13-10z" fill="#92400e" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M22 20h14" fill="none" stroke="#1b2a4a" strokeWidth="2" />
        <path d="M40 10q4-4 2-8M46 12q4-3 3-7" fill="none" stroke="#22c55e" strokeWidth="2.4" strokeLinecap="round" />
      </symbol>
      <symbol id="sa-cowpat" viewBox="0 0 60 60">
        <path d="M10 38c-2-10 8-18 20-18s16-8 24-2c8 6 2 20-8 22-4 1-2 6-10 6-10 0-8-4-14-2-8 2-14-2-12-6z" fill="#92400e" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <path d="M22 30q6-2 10 2M34 34q4-2 8 0" fill="none" stroke="#1b2a4a" strokeWidth="1.8" />
      </symbol>
      <symbol id="sa-duck" viewBox="0 0 60 60">
        {/* Redrawn (Aug 2026, player feedback: "the duck doesn't have a head") — the
            handoff's single continuous body/head silhouette read as a headless blob at
            48px in-game. A separate head circle sitting forward and above the body, with
            its own outline, is unmistakable at speed the way the original wasn't. */}
        <ellipse cx="30" cy="40" rx="19" ry="14" fill="#fbbf24" stroke="#1b2a4a" strokeWidth="3.5" />
        <circle cx="40" cy="24" r="12" fill="#fbbf24" stroke="#1b2a4a" strokeWidth="3.5" />
        <path d="M50 24l8 2-6 5z" fill="#f97316" stroke="#1b2a4a" strokeWidth="2.4" strokeLinejoin="round" />
        <circle cx="44" cy="20" r="2.2" fill="#1b2a4a" />
        <path d="M14 44q8 5 15 1" fill="none" stroke="#1b2a4a" strokeWidth="1.8" strokeLinecap="round" />
      </symbol>
      <symbol id="sa-doughnut" viewBox="0 0 60 60">
        <path fillRule="evenodd" d="M30,6 a24,24 0 1,0 0.1,0 z M30,20 a10,10 0 1,0 0.1,0 z" fill="#fbbf24" stroke="#1b2a4a" strokeWidth="3.5" />
        <path d="M12 22c6-6 12-8 18-8s12 2 18 8" fill="none" stroke="#ef4444" strokeWidth="6" strokeLinecap="round" />
      </symbol>
      <symbol id="sa-flat-cap" viewBox="0 0 60 60">
        <path d="M12 34c0-12 10-20 20-20 14 0 20 10 20 16 4 1 6 4 6 6 0 3-3 4-6 4H14c-3 0-6-1-6-3 0-2 2-3 4-3z" fill="#2B3350" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round" />
        <circle cx="30" cy="18" r="3" fill="#FFC629" stroke="#1b2a4a" strokeWidth="1.8" />
      </symbol>
      {/* LB-329: the top hat the crow loses on defeat — the one symbol taken from the pack's
          snack-attack-drop-objects.svg (17 Sep 2026), inlined like the drops above. */}
      <symbol id="sa-top-hat" viewBox="0 0 60 60">
      <g fill="#1b2a4a" stroke="#1b2a4a" strokeWidth="3.5" strokeLinejoin="round">
      <rect x="16" y="8" width="28" height="26" rx="3"></rect>
      <rect x="8" y="32" width="44" height="8" rx="4"></rect>
      <rect x="16" y="25" width="28" height="6" fill="#FFC629" stroke="none"></rect>
      </g>
      </symbol>
      {/* The Greedy Crow himself — 200×260 like Piggington, from greedy-crow-sprite.svg.
          Included: both bases, three expressions, three wing frames. Excluded by scope:
          cx-greedy (coin pupil, §0.1) and cw-coin-sack (currency imagery). cw-top-hat joined
          them under LB-329 (17 Sep 2026): the crow wears it from the start of every round
          and loses it, once, on the defeat drop — see the crow render below. */}
      <symbol id="cw-base" viewBox="0 0 200 260">
        <g stroke="#1b2a4a" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round">
          <path d="M162 138l34-26 2 46-34-6z" fill="#232B45" />
          <path d="M180 118l-14 12M186 148l-18-8" fill="none" strokeWidth="4" />
          <path d="M98 196v18M126 196v18" fill="none" />
          <path d="M86 216h24M114 216h24" fill="none" stroke="#FFC629" strokeWidth="8" />
          <ellipse cx="112" cy="150" rx="54" ry="48" fill="#2B3350" />
          <path d="M74 168c14 14 44 16 60 2" fill="none" strokeWidth="5" stroke="#3D4870" />
          <circle cx="118" cy="74" r="38" fill="#2B3350" />
          <path d="M84 62l-42 16 42 18z" fill="#FFC629" />
          <path d="M84 78H44" fill="none" strokeWidth="5" />
        </g>
      </symbol>
      <symbol id="cw-base-squawk" viewBox="0 0 200 260">
        <g stroke="#1b2a4a" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round">
          <path d="M162 138l34-26 2 46-34-6z" fill="#232B45" />
          <path d="M180 118l-14 12M186 148l-18-8" fill="none" strokeWidth="4" />
          <path d="M98 196v18M126 196v18" fill="none" />
          <path d="M86 216h24M114 216h24" fill="none" stroke="#FFC629" strokeWidth="8" />
          <ellipse cx="112" cy="150" rx="54" ry="48" fill="#2B3350" />
          <path d="M74 168c14 14 44 16 60 2" fill="none" strokeWidth="5" stroke="#3D4870" />
          <circle cx="118" cy="74" r="38" fill="#2B3350" />
          <path d="M86 58l-46 6 46 14z" fill="#FFC629" />
          <path d="M86 82l-40 22 44 8z" fill="#FFC629" />
          <path d="M84 76c-10 2-18 8-22 16" fill="#F5A3A8" stroke="none" />
        </g>
      </symbol>
      <symbol id="cx-mean" viewBox="0 0 200 260">
        <ellipse cx="112" cy="68" rx="13" ry="11" fill="#FFFDF6" stroke="#1b2a4a" strokeWidth="5" />
        <circle cx="106" cy="69" r="5.5" fill="#1b2a4a" />
        <path d="M130 46L94 60" fill="none" stroke="#1b2a4a" strokeWidth="9" strokeLinecap="round" />
      </symbol>
      <symbol id="cx-sneaky" viewBox="0 0 200 260">
        <ellipse cx="112" cy="68" rx="13" ry="11" fill="#FFFDF6" stroke="#1b2a4a" strokeWidth="5" />
        <circle cx="105" cy="73" r="5" fill="#1b2a4a" />
        <path d="M99 61h26" fill="none" stroke="#1b2a4a" strokeWidth="10" strokeLinecap="round" />
        <path d="M132 48L102 56" fill="none" stroke="#1b2a4a" strokeWidth="8" strokeLinecap="round" />
      </symbol>
      <symbol id="cx-furious" viewBox="0 0 200 260">
        <ellipse cx="112" cy="70" rx="11" ry="9" fill="#FFFDF6" stroke="#1b2a4a" strokeWidth="5" />
        <circle cx="107" cy="71" r="4" fill="#1b2a4a" />
        <path d="M132 44L92 62" fill="none" stroke="#1b2a4a" strokeWidth="11" strokeLinecap="round" />
        <path d="M146 40l12-8M150 54l14-2" fill="none" stroke="#1b2a4a" strokeWidth="4.5" strokeLinecap="round" />
      </symbol>
      <symbol id="cw-wing-up" viewBox="0 0 200 260">
        <g stroke="#1b2a4a" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round">
          <path d="M96 128C74 96 56 62 62 44c16-6 44 40 56 74z" fill="#232B45" />
          <path d="M76 62l16 34M90 50l16 40" fill="none" strokeWidth="4" />
        </g>
      </symbol>
      <symbol id="cw-wing-mid" viewBox="0 0 200 260">
        <g stroke="#1b2a4a" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round">
          <path d="M100 132C68 130 34 132 24 142c10 12 52 18 82 10z" fill="#232B45" />
          <path d="M44 136l4 14M64 134l2 16" fill="none" strokeWidth="4" />
        </g>
      </symbol>
      {/* LB-329: his top hat, z 40 (crow manifest "props"), from greedy-crow-sprite.svg —
          the only symbol taken from that file. */}
      <symbol id="cw-top-hat" viewBox="0 0 200 260">
      <g fill="#1b2a4a" stroke="#1b2a4a" strokeWidth="7" strokeLinejoin="round">
      <rect x="96" y="8" width="60" height="34" rx="5"></rect>
      <rect x="82" y="40" width="88" height="10" rx="5"></rect>
      <rect x="96" y="30" width="60" height="9" fill="#FFC629" stroke="none"></rect>
      </g>
      </symbol>
      <symbol id="cw-wing-down" viewBox="0 0 200 260">
        <g stroke="#1b2a4a" strokeWidth="7" strokeLinejoin="round" strokeLinecap="round">
          <path d="M100 130C78 158 58 186 62 200c18 4 42-40 52-64z" fill="#232B45" />
          <path d="M78 184l14-30M92 194l12-34" fill="none" strokeWidth="4" />
        </g>
      </symbol>
    </svg>
  );
}

/* ------------------------------ how-to-play legend ------------------------------ */

// Replaces the old single sentence of instructions (player feedback, Aug 2026: "make it
// clearer, use icons"). Every falling object gets its own icon, a one-word read, and a
// colour that does double duty as the teaching signal — green "catch it", rose "avoid it",
// amber "rare — special!" — so the good/bad split is visible at a glance before a child
// reads a single word. Order matches rough spawn frequency: common food first, rarest last.
const LEGEND_ITEMS = [
  { symbol: "sa-apple", label: "Fruit & veg", tag: "+1", tone: "good" },
  { symbol: "sa-cupcake", label: "Treats", tag: "+3 / +5", tone: "good" },
  { symbol: "sa-rotten", label: "Rotten food", tag: "dodge!", tone: "bad" },
  { symbol: "sa-boot", label: "Boots & cans", tag: "−1 life", tone: "bad" },
  { symbol: "sa-tinhat", label: "Tin hat", tag: "blocks a hit!", tone: "rare" },
  { symbol: "sa-bandage", label: "Bandage", tag: "+1 life", tone: "rare" },
];
const LEGEND_TONE_CLASS = {
  good: "bg-emerald-50 border-emerald-200 text-emerald-700",
  bad: "bg-rose-50 border-rose-200 text-rose-700",
  rare: "bg-amber-50 border-amber-200 text-amber-700",
};

function HowToPlayLegend() {
  return (
    <div data-testid="sc-legend" className="grid grid-cols-3 gap-1.5 w-full max-w-xs mb-3">
      {LEGEND_ITEMS.map((it) => (
        <div key={it.symbol} data-testid={`sc-legend-${it.symbol}`} className={`rounded-xl border px-1 py-2 flex flex-col items-center gap-0.5 ${LEGEND_TONE_CLASS[it.tone]}`}>
          <svg width="26" height="26" aria-hidden="true"><use href={`#${it.symbol}`} /></svg>
          <span className="text-[10px] font-extrabold leading-tight text-center">{it.label}</span>
          <span className="text-[9px] font-bold opacity-80">{it.tag}</span>
        </div>
      ))}
    </div>
  );
}

/* ------------------------------ Arcade card (Kid Home) ------------------------------ */

// Full-width banner directly below the Pig Card — same pig, same cosmetics, natural
// adjacency — styled as a sibling of LearnEarnTeaser but VIOLET-gradient, deliberately
// distinct from Learn & Earn's amber so the two banners are instantly distinguishable
// ("this one doesn't pay"). "Piggington's Arcade" as the card name future-proofed a second
// mini-game without new Home furniture; Pig Mountain (LB-256) is that game, and the card now
// opens the picker in ArcadePicker.jsx instead of Snack Attack directly. Stays visible in Oinks-off households (the game
// doesn't depend on the Oinks economy, only the pig art — base-only if no cosmetics);
// hidden only by the parent's own Arcade toggle.
export function ArcadeCard({ child, learning, onOpen }) {
  if (!arcadeEnabledFor(learning)) return null;
  const locked = arcadeWarmupModeFor(learning) === "warmup" && !warmupDoneToday(child.id);
  // LB-256: the arcade holds two games now, so the card names both and each game's best
  // moved to its own row on the picker (ArcadePicker.jsx).
  const subtitle = locked ? "Do today's Brain Warm-Up to play!" : "Snack Attack and Pig Mountain";
  return (
    <button data-testid="arcade-card" onClick={onOpen} className="w-full h-full text-left kc kc-play rounded-lg p-3 ks-tile">
      <div className="ks-tile-head">
      <div className="flex-shrink-0 w-9 h-[46px]">
        {/* LB-168 (Alan): it is Piggington's arcade — he is the host on the sign, whoever the
            child plays as. Inside the game the player and the results screen are the child's
            own character (characterBase + worn), unchanged. */}
        <Piggington expression="fx-happy" base="pg-base" className="w-9 h-[46px]" />
      </div>
        <div className="font-extrabold text-sm kc-title min-w-0">Piggington's Arcade</div>
      </div>
      <div className="min-w-0">
        <div data-testid="arcade-card-subtitle" className="text-xs font-semibold text-slate-600">{subtitle}</div>
      </div>
    </button>
  );
}

/* ------------------------------ Brain Warm-Up screen ------------------------------ */

// The daily gate (scope §2.3): 3 correct IN TOTAL, not consecutive, unlimited attempts —
// there is deliberately NO fail state (a gate that can be failed turns dead-time fun into a
// frustration trap for exactly the age group most likely to be locked out). A wrong answer
// gets "Not quite — try this one!" and a fresh question. Uses the Learn & Earn numeric-pad
// pattern so a child who's played TT Streaks already knows how to drive it. Answers pay
// nothing and record nothing — progress lives in component state only, so abandoning
// mid-warm-up restarts at 0 of 3.
export function WarmupScreen({ child, learning, scTest, onDone, onClose }) {
  const [correct, setCorrect] = useState(0);
  const [served, setServed] = useState(0); // question index — advances on every answer, right or wrong
  const [input, setInput] = useState("");
  const [retryLine, setRetryLine] = useState(false);
  const question = useMemo(
    () => (scTest ? scTestWarmupQuestion(served) : warmupQuestion(child, learning)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [served, scTest, child.id]
  );

  function submit() {
    if (input.trim() === "") return;
    const right = Number(input) === question.answer;
    setInput("");
    if (right) {
      const n = correct + 1;
      setRetryLine(false);
      if (n >= WARMUP_TARGET) {
        try { localStorage.setItem(warmupKeyFor(child.id), todayStr()); } catch {}
        onDone();
        return;
      }
      setCorrect(n);
    } else {
      setRetryLine(true);
    }
    setServed((i) => i + 1);
  }

  return (
    <div data-testid="warmup-screen" className="bg-white w-full h-full flex flex-col items-center overflow-y-auto">
      {/* Safe-area top inset (iPhone notch/Dynamic Island): without it the title and the
          Close ✕ render underneath the status bar in the installed edge-to-edge PWA,
          leaving the Close button visually obscured and effectively untappable. Same
          calc(env(safe-area-inset-top) + fixed) pattern as the ready screen's ✕ and the
          in-game HUD. */}
      <div className="w-full max-w-sm px-5 pb-8 flex flex-col items-center" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 20px)" }}>
        <div className="w-full flex items-start justify-between">
          <div>
            <div className="font-extrabold text-lg text-slate-900">🧠 Brain Warm-Up</div>
            <div className="text-xs text-slate-500 font-semibold">Answer 3 to open the arcade for today!</div>
          </div>
          <button aria-label="Close" className="text-slate-400 text-xl font-bold px-1.5" onClick={onClose}>✕</button>
        </div>
        <div className="my-2">
          <Piggington expression="fx-thinking" cosmetics={wornPiggingtonCosmetics(child)} base={equippedCharacterBaseId(child)} className="w-16 h-[83px]" />
        </div>
        <div className="w-full bg-slate-50 border border-slate-100 rounded-2xl py-4 text-center">
          <div data-testid="warmup-prompt" className="font-extrabold text-3xl text-slate-900">{withAnswerPrompt(question.prompt)}</div>
        </div>
        {retryLine && (
          <div data-testid="warmup-retry" className="text-sm font-bold text-amber-600 mt-2">Not quite — try this one!</div>
        )}
        <div className="w-full mt-3">
          <NumericKeypad
            value={input}
            disabled={false}
            allowDecimal
            allowNegative
            onEnter={submit}
            onDigit={(d) => setInput((v) => (v.length < 6 ? v + d : v))}
            onBackspace={() => setInput((v) => v.slice(0, -1))}
            onDecimal={() => setInput((v) => (v.includes(".") || v.length >= 6 ? v : v + "."))}
            onToggleSign={() => setInput((v) => (v.startsWith("-") ? v.slice(1) : `-${v}`))}
          />
          <button className="w-full bg-pink-500 text-white font-bold rounded-xl py-2.5 text-sm mt-3" onClick={submit}>Check</button>
        </div>
        <div className="flex items-center gap-2 mt-4" data-testid="warmup-dots">
          {Array.from({ length: WARMUP_TARGET }, (_, i) => (
            <span key={i} className={`w-3 h-3 rounded-full ${i < correct ? "bg-pink-500" : "bg-slate-200"}`} />
          ))}
          <span data-testid="warmup-count" className="text-xs font-bold text-slate-500 ml-1">{correct} of {WARMUP_TARGET}</span>
        </div>
      </div>
    </div>
  );
}

/* ------------------------------ the game ------------------------------ */

const PIG_W = 104; // ~96–110px per the approved playfield mockup
// W1.14 — widest the playing area is allowed to get (tablets/landscape). Phone portrait
// widths sit well under this, so the clamp is invisible there; see SnackCatchOverlay.
const MAX_PLAYFIELD_W = 560;
const PIG_H = Math.round(PIG_W * 1.3);
// Piggington's gap from the very bottom of the field (player feedback, Aug 2026: a dragging
// thumb was resting right on top of him at the old 6px gap, hiding him mid-catch). Raising
// this is the whole fix — the field's full-height drag surface is unchanged, so a thumb
// tracking left/right now naturally lands in the strip below him instead of on him.
const PIG_BOTTOM_GAP = 72;
const OBJ_SIZE = 48;
const BASE_FALL_FRACTION = 0.26; // of playfield height, per second, before stage/mode multipliers
const KEYBOARD_SPEED = 340; // px/s, Chromebook arrow keys
const MAX_OBJECTS = 12;
// Greedy Crow render constants (GREEDY_CROW_SCOPE.md §3): 200×260 viewBox at 0.39 scale —
// ¾ of PIG_W, a substantial antagonist that doesn't eat the phone-height playfield. The bob
// centres on CROW_TOP_FRACTION of field height; he exits at CROW_FLEE_SPEED when defeated.
const CROW_W = 78;
const CROW_H = 101;
const CROW_TOP_FRACTION = 0.16;
const CROW_FLEE_SPEED = 620; // px/s
const CROW_FLAP_MS = 90; // handoff's suggested frame time…
const CROW_GLIDE_FLAP_MS = 160; // …slowing to a lazy glide between drops
const CROW_FLAP_FRAMES = ["cw-wing-up", "cw-wing-mid", "cw-wing-down", "cw-wing-mid"];
const CROW_WING_IDS = ["cw-wing-up", "cw-wing-mid", "cw-wing-down"];
const CROW_EXPR_IDS = ["cx-mean", "cx-sneaky", "cx-furious"];

// The playing surface plus paused/game-over overlays. All per-frame motion happens by
// writing transforms on refs inside one rAF loop (hard-stopped on pause/unmount);
// React state changes only on discrete events. Collision is an axis-aligned box test
// against the pig's head/snout zone in game coordinates — no getBoundingClientRect
// per frame.
function GameScreen({ child, learning, scTest, scBoss, onQuit }) {
  const characterBase = equippedCharacterBaseId(child);
  const mode = modeForAge(effectiveAge(child));
  const worn = wornPiggingtonCosmetics(child);
  const pigName = pigDisplayNameFor(child);

  const fieldRef = useRef(null);
  const pigRef = useRef(null);
  const sizeRef = useRef({ w: 360, h: 640 });
  const pigXRef = useRef(180); // pig centre x, game coords
  const pigTargetRef = useRef(null); // pointer target x, or null
  const keysRef = useRef({ left: false, right: false });
  const runRef = useRef(initialRunState());
  const objDataRef = useRef(new Map()); // id -> { kind, x, y, speed, caught, boss }
  const objElsRef = useRef(new Map()); // id -> element
  const nextIdRef = useRef(1);
  const spawnAccRef = useRef(0);
  const spawnedCountRef = useRef(0);
  const lastTsRef = useRef(null);
  const rafRef = useRef(null);
  const runningRef = useRef(false);
  // Crow Wave (Boss v2, GREEDY_CROW_SCOPE.md — supersedes the v1 fixed-pattern gauntlet).
  // One ref object carries the whole wave: phase ("off" → "patrol" → "flee" → "off"),
  // elapsed wave time, the drop-cadence accumulator, the squawk-telegraph timestamp, and
  // `pending` — crow-tagged objects still in flight, so the wave's outcome (and the hat) is
  // only judged once every drop has actually landed, exactly the v1 pending rule. All
  // per-frame motion lives on refs; crowUi is the single discrete React flag that mounts
  // the crow, the sky tint, and the timer bar.
  const crowRef = useRef({ phase: "off", elapsed: 0, dropAcc: 0, telegraphAt: null, pending: 0, resolved: true, heartsAtStart: STARTING_HEARTS, waveMs: 0, dropMs: 0, fleeX: 0, fleeDir: 1, hatX: null });
  const crowElRef = useRef(null); // positioned outer div
  const crowFlipRef = useRef(null); // inner wrapper, scaleX(-1) when flying right (he faces left natively)
  const crowLayerRefs = useRef({}); // layer <use> elements by key — display-toggled in the rAF loop
  const bossBarRef = useRef(null); // the timer bar's fill, width-driven per frame
  // `&scBoss=1` only: the normal spawner stays silent after the wave resolves, so the final
  // score is exactly BOSS_CLEAR_BONUS_POINTS + the hat with nothing random for the spec to race.
  const silencedRef = useRef(false);
  // `&scTest=1`'s deterministic forced-spawn sequence (all dead-centred on the pig's
  // starting x, so no drag input is needed): tin hat first — so a spec can assert the shield
  // badge appears — then two hazards, the first of which the shield absorbs for free and the
  // second of which ends the (1-heart) run on schedule.
  const scTestSequenceRef = useRef(["tinhat", "boot", "boot"]);

  const [hud, setHud] = useState({ score: scBoss ? SC_BOSS_TEST.startScore : 0, hearts: scTest && !scBoss ? 1 : STARTING_HEARTS, stage: scBoss ? stageForScore(SC_BOSS_TEST.startScore) : 1, shielded: false });
  const [objs, setObjs] = useState([]); // [{ id, kind }] — membership only; positions live on refs
  const [floats, setFloats] = useState([]); // [{ id, text, cls, x, y }]
  const [expr, setExpr] = useState("fx-happy");
  const [levelBanner, setLevelBanner] = useState(null);
  const [crowUi, setCrowUi] = useState(false); // crow + tint + timer bar mounted (discrete flips only)
  // LB-329 (Alan, 17 Sep: one hat per boss wave): the crow wears a fresh top hat into EVERY
  // Crow Wave and loses it on that wave's defeat drop, so he is bare-headed only between
  // losing it and the next wave. Ref for the rule, state for the draw.
  const hatWornRef = useRef(true);
  const [crowHat, setCrowHat] = useState(true);
  // scBoss is a wave count (ChildView parses `&scBoss=N`): waves still to start after the
  // first, each begun as soon as the previous crow has left the field. Preview-only.
  const scBossWavesLeftRef = useRef(scBoss ? scBoss - 1 : 0);
  const [screen, setScreen] = useState("playing"); // playing | paused | gameover
  const [gameOver, setGameOver] = useState(null); // { score, best, newBest }
  const [showHint, setShowHint] = useState(() => {
    try { return !localStorage.getItem(HINT_KEY); } catch { return false; }
  });
  const exprTimerRef = useRef(null);
  const bannerTimerRef = useRef(null);

  // `&scTest=1` (honoured only in preview mode — the caller enforces that): 1 heart and a
  // forced 3-step spawn sequence (tinhat, boot, boot — see scTestSequenceRef above), all
  // dead-centred on the pig's own x, so a spec can reach game-over deterministically — and
  // assert the shield absorbing one hazard along the way — without simulating gameplay. The
  // rAF loop itself is not E2E-tested.
  useEffect(() => {
    // scBoss takes precedence over scTest's 1-heart run (the two combine in specs — scTest
    // still seeds the warm-up questions): a crow-wave test starts at SC_BOSS_TEST.startScore
    // on full hearts, already inside the wave (started from the boot effect below).
    if (scBoss) {
      runRef.current = { ...initialRunState(), score: SC_BOSS_TEST.startScore, stage: stageForScore(SC_BOSS_TEST.startScore) };
    } else {
      runRef.current = { ...initialRunState(), hearts: scTest ? 1 : STARTING_HEARTS };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const flashExpr = useCallback((e, ms = 700) => {
    setExpr(e);
    if (exprTimerRef.current) clearTimeout(exprTimerRef.current);
    exprTimerRef.current = setTimeout(() => setExpr("fx-happy"), ms);
  }, []);

  const addFloat = useCallback((text, cls, x, y) => {
    const id = `f${Date.now()}-${Math.random()}`;
    setFloats((f) => [...f, { id, text, cls, x, y }]);
    setTimeout(() => setFloats((f) => f.filter((p) => p.id !== id)), 900);
  }, []);

  const removeObject = useCallback((id, popped) => {
    objDataRef.current.delete(id);
    if (popped) {
      const el = objElsRef.current.get(id);
      if (el) el.classList.add("sc-pop");
      setTimeout(() => setObjs((o) => o.filter((x) => x.id !== id)), 160);
    } else {
      setObjs((o) => o.filter((x) => x.id !== id));
    }
  }, []);

  const endRun = useCallback(() => {
    runningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    const score = runRef.current.score;
    const prev = readBest(child.id);
    const newBest = !prev || score > prev.best;
    if (newBest) {
      try { localStorage.setItem(bestKeyFor(child.id), JSON.stringify({ best: score, at: todayStr() })); } catch {}
    }
    setGameOver({ score, best: newBest ? score : prev.best, newBest });
    setScreen("gameover");
  }, [child.id]);

  // Starts a Crow Wave (Boss v2): the Greedy Crow flies in, the random spawner stands down
  // (see step), and for waveMs he drops from his own patrol position. Hearts-at-entry are
  // remembered to judge the clear tier later. scBoss uses SC_BOSS_TEST's short deterministic
  // parameters instead of the mode's real ones.
  const startCrowWave = useCallback(() => {
    crowRef.current = {
      phase: "patrol",
      elapsed: 0,
      dropAcc: 0,
      telegraphAt: null,
      pending: 0,
      resolved: false,
      heartsAtStart: runRef.current.hearts,
      waveMs: scBoss ? SC_BOSS_TEST.waveMs : CROW_WAVE_MS[mode],
      dropMs: scBoss ? SC_BOSS_TEST.dropMs : CROW_DROP_MS[mode],
      fleeX: 0,
      fleeDir: 1,
      hatX: null,
    };
    spawnAccRef.current = 0; // normal spawning resumes on a crisp beat after the wave
    hatWornRef.current = true; // LB-329: a fresh hat for every wave
    setCrowHat(true);
    setCrowUi(true);
    setLevelBanner("⚡ Crow Attack! Dodge the junk!");
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    bannerTimerRef.current = setTimeout(() => setLevelBanner(null), 1800);
  }, [mode, scBoss]);

  // The defeat drop: the crow's own top hat, spawned at his exit x (scope §0.4 — it's HIS,
  // which is the whole joke; LB-329 made it the hat he actually wears) and falling through the
  // ordinary object pipeline like anything else. scBoss pins it to the pig's x so it's
  // auto-caught deterministically.
  const spawnHat = useCallback((x) => {
    const { w, h } = sizeRef.current;
    const cx = Math.max(OBJ_SIZE / 2, Math.min(w - OBJ_SIZE / 2, x));
    const params = stageParams(runRef.current.stage, mode);
    const speed = h * BASE_FALL_FRACTION * params.speed * (OBJECT_KINDS[CROW_HAT_KIND]?.speedFactor || 1);
    const id = `o${nextIdRef.current++}`;
    objDataRef.current.set(id, { kind: CROW_HAT_KIND, x: cx, y: -OBJ_SIZE, speed, boss: false });
    setObjs((o) => [...o, { id, kind: CROW_HAT_KIND }]);
  }, [mode]);

  // Judges the wave once the timer is up AND every crow-tagged drop has actually resolved
  // (caught or floor) — the v1 pending rule. Two tiers (scope §0.5): SURVIVED — the crow is
  // beaten, so the hat drops, always; SURVIVED WELL (≤1 heart lost, a shield-absorbed hit
  // never counts) — the flat bonus and Piggington's celebration on top.
  const resolveCrowWave = useCallback(() => {
    const crow = crowRef.current;
    if (crow.resolved) return;
    crow.resolved = true;
    const heartsLost = Math.max(0, crow.heartsAtStart - runRef.current.hearts);
    if (bossWaveCleared(heartsLost)) {
      const score = runRef.current.score + BOSS_CLEAR_BONUS_POINTS;
      runRef.current = { ...runRef.current, score, stage: stageForScore(score) };
      setHud({ score: runRef.current.score, hearts: runRef.current.hearts, stage: runRef.current.stage, shielded: runRef.current.shielded });
      addFloat(`Crow defeated! +${BOSS_CLEAR_BONUS_POINTS} 🎉`, "combo", pigXRef.current, sizeRef.current.h - PIG_H - 60);
      flashExpr("fx-celebrating", 1200);
    } else {
      addFloat("The crow flees!", "combo", pigXRef.current, sizeRef.current.h - PIG_H - 60);
    }
    // LB-329: the hat leaves him at this moment — bare-headed until the next wave re-arms it
    // in startCrowWave — and only if he is still wearing it. Every survived wave (boss stages
    // 4, 7, 10…) pays the hat, the cadence the game always had.
    if (hatWornRef.current) {
      hatWornRef.current = false;
      setCrowHat(false);
      spawnHat(scBoss ? pigXRef.current : (crow.hatX ?? pigXRef.current));
    }
    if (scBoss) silencedRef.current = true;
  }, [addFloat, flashExpr, spawnHat, scBoss]);

  const handleHit = useCallback((id, kindId) => {
    const { next, event } = applyCatch(runRef.current, kindId, mode);
    const prevStage = runRef.current.stage;
    runRef.current = next;
    const data = objDataRef.current.get(id);
    const fx = data ? data.x : pigXRef.current;
    const fy = data ? data.y : sizeRef.current.h - PIG_H - 40;
    const wasBoss = !!data?.boss;
    const popped = event.type === "good" || event.type === "treat" || event.type === "shield" || event.type === "shieldBonus" || event.type === "extra" || event.type === "extraFull";
    removeObject(id, popped);
    if (event.type === "good" || event.type === "treat") {
      addFloat(`+${event.points}`, "good", fx, fy);
      if (event.comboBonus) addFloat(`Combo! +${event.comboBonus}`, "combo", fx, fy - 30);
      if (kindId === "acorn") flashExpr("fx-wow");
      if (pigRef.current) {
        pigRef.current.classList.remove("sc-bounce");
        void pigRef.current.offsetWidth; // restart the animation
        pigRef.current.classList.add("sc-bounce");
      }
    } else if (event.type === "rotten") {
      // Rotten ≠ hazard: costs score, never a heart. Green yuck puff + red float + a brief
      // startled face.
      addFloat(`Yuck! −${rottenPenalty(mode)}`, "bad", fx, fy);
      flashExpr("fx-oops");
    } else if (event.type === "hazard") {
      // Startled (fx-oops), never sad — no sad-pig expression exists in this game, by rule.
      flashExpr("fx-oops");
      if (fieldRef.current) {
        fieldRef.current.classList.remove("sc-flash");
        void fieldRef.current.offsetWidth;
        fieldRef.current.classList.add("sc-flash");
      }
    } else if (event.type === "shield") {
      addFloat("🪖 Shielded!", "good", fx, fy);
      flashExpr("fx-wow");
      if (pigRef.current) {
        pigRef.current.classList.remove("sc-bounce");
        void pigRef.current.offsetWidth;
        pigRef.current.classList.add("sc-bounce");
      }
    } else if (event.type === "shieldBonus") {
      addFloat(`+${event.points}`, "good", fx, fy);
      addFloat("Already shielded!", "combo", fx, fy - 30);
    } else if (event.type === "extra") {
      addFloat("+1 ❤️", "good", fx, fy);
      flashExpr("fx-wow");
    } else if (event.type === "extraFull") {
      addFloat("Already full!", "combo", fx, fy);
    } else if (event.type === "shieldBroken") {
      // The tin hat took the hit instead of a heart — a relieved beat, not a startled one,
      // and an amber "blocked" flash rather than the full red hazard flash.
      addFloat("Hat saved you!", "good", fx, fy);
      flashExpr("fx-wow");
      if (fieldRef.current) {
        fieldRef.current.classList.remove("sc-shield-flash");
        void fieldRef.current.offsetWidth;
        fieldRef.current.classList.add("sc-shield-flash");
      }
    }
    if (next.stage > prevStage && crowRef.current.resolved) {
      // Stage advances are silent while a crow wave is live — the wave IS the event, and a
      // "Level N!" banner would clobber the attack banner. A boss stage reached mid-wave
      // (only possible via extreme treat-catching) simply waits for its next trigger.
      if (isBossStage(next.stage)) {
        startCrowWave();
      } else {
        setLevelBanner(`Level ${next.stage}! Faster snacks!`);
        if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
        bannerTimerRef.current = setTimeout(() => setLevelBanner(null), 1600);
      }
    }
    setHud({ score: next.score, hearts: next.hearts, stage: next.stage, shielded: next.shielded });
    if (wasBoss) {
      const crow = crowRef.current;
      crow.pending = Math.max(0, crow.pending - 1);
      // Timer-up, not "phase === flee": the crow's flight off-screen is a separate,
      // faster-running animation (CROW_FLEE_SPEED) that can finish well before a
      // slow-falling drop from late in the wave actually lands. Gating on the visual phase
      // string missed that drop's landing entirely and left the wave stuck forever — this
      // checks the real clock instead, which stays true regardless of where the crow
      // sprite currently is. resolveCrowWave's own `resolved` guard makes this safe to call
      // from every landing site without double-firing.
      if (!crow.resolved && crow.elapsed >= crow.waveMs && crow.pending === 0) resolveCrowWave();
    }
    if (next.hearts <= 0) endRun();
  }, [mode, addFloat, flashExpr, removeObject, endRun, startCrowWave, resolveCrowWave]);

  const handleFloor = useCallback((id, kindId) => {
    const data = objDataRef.current.get(id);
    const wasBoss = !!data?.boss;
    const cls = OBJECT_KINDS[kindId]?.cls;
    removeObject(id, false);
    if (wasBoss) {
      const crow = crowRef.current;
      crow.pending = Math.max(0, crow.pending - 1);
      // See the identical comment in handleHit — timer-up, not the visual phase, is the
      // correct signal here too.
      if (!crow.resolved && crow.elapsed >= crow.waveMs && crow.pending === 0) resolveCrowWave();
    }
    // Missed rotten food, hazards, tin hats and bandages are never penalised — dodging (or
    // simply not needing) them is the point. Missed good food costs a heart only in Standard.
    // Crow drops are exempt wholesale (wasBoss): during a wave the job is DODGING, so the
    // crow's ducks and doughnuts are temptations, never obligations — a missed one costs
    // nothing in either mode (GREEDY_CROW_SCOPE.md §4, "greed stays tempting"). This is a
    // deliberate change from v1, whose pattern-woven apples did punish misses.
    if (wasBoss) return;
    if (cls !== "good" && cls !== "treat") return;
    const { next, event } = applyMissedGood(runRef.current, mode);
    if (event.type === "none") return;
    runRef.current = next;
    flashExpr("fx-oops");
    setHud({ score: next.score, hearts: next.hearts, stage: next.stage, shielded: next.shielded });
    if (next.hearts <= 0) endRun();
  }, [mode, removeObject, flashExpr, endRun, resolveCrowWave]);

  const spawnObject = useCallback(() => {
    if (objDataRef.current.size >= MAX_OBJECTS) return;
    const { w, h } = sizeRef.current;
    const params = stageParams(runRef.current.stage, mode);
    let kind;
    let x = OBJ_SIZE / 2 + Math.random() * (w - OBJ_SIZE);
    const seq = scTestSequenceRef.current;
    if (scTest && !scBoss && spawnedCountRef.current < seq.length) {
      // Forced sequence, dead-centred on the pig — deterministic even before it moves.
      // (Suppressed under scBoss, which takes precedence — the crow wave is the sequence.)
      kind = seq[spawnedCountRef.current];
      x = pigXRef.current;
    } else {
      kind = pickSpawnKind(Math.random, params);
    }
    spawnedCountRef.current += 1;
    const speed = h * BASE_FALL_FRACTION * params.speed * (OBJECT_KINDS[kind]?.speedFactor || 1);
    const id = `o${nextIdRef.current++}`;
    objDataRef.current.set(id, { kind, x, y: -OBJ_SIZE, speed, boss: false });
    setObjs((o) => [...o, { id, kind }]);
  }, [mode, scTest, scBoss]);

  // One crow drop, released from his current x (the whole reason his movement matters —
  // scope §2): weighted from the mode's table live, forced to SC_BOSS_TEST.dropKind under
  // scBoss. Crow-tagged (`boss: true`) so `pending` gates the wave's resolution.
  const spawnCrowDrop = useCallback((crowX, crowY) => {
    if (objDataRef.current.size >= MAX_OBJECTS) return;
    const { w, h } = sizeRef.current;
    const kind = scBoss ? SC_BOSS_TEST.dropKind : pickCrowDrop(Math.random, crowDropTableFor(mode));
    // scBoss: every drop lands a full pig-width beside the stationary pig — a guaranteed,
    // penalty-free miss (crow drops never punish the floor), so the run's score can't move
    // until the deterministic +bonus +hat at the end.
    const rawX = scBoss ? pigXRef.current + PIG_W : crowX;
    const x = Math.max(OBJ_SIZE / 2, Math.min(w - OBJ_SIZE / 2, rawX));
    const params = stageParams(runRef.current.stage, mode);
    const speed = h * BASE_FALL_FRACTION * params.speed * (OBJECT_KINDS[kind]?.speedFactor || 1);
    const id = `o${nextIdRef.current++}`;
    crowRef.current.pending += 1;
    // Released at his claws — the dropPoint anchor sits ~87% down the sprite (handoff).
    objDataRef.current.set(id, { kind, x, y: crowY + CROW_H * 0.87, speed, boss: true });
    setObjs((o) => [...o, { id, kind }]);
  }, [mode, scBoss]);

  const step = useCallback((ts) => {
    if (!runningRef.current) return;
    if (lastTsRef.current == null) lastTsRef.current = ts;
    const dt = Math.min(50, ts - lastTsRef.current); // clamp — background tab jumps never teleport objects
    lastTsRef.current = ts;
    const { w, h } = sizeRef.current;

    // Pig movement: pointer target (light smoothing) or held arrow keys.
    let px = pigXRef.current;
    if (pigTargetRef.current != null) {
      px += (pigTargetRef.current - px) * Math.min(1, dt / 70);
    } else if (keysRef.current.left || keysRef.current.right) {
      px += ((keysRef.current.right ? 1 : 0) - (keysRef.current.left ? 1 : 0)) * KEYBOARD_SPEED * (dt / 1000);
    }
    px = Math.max(PIG_W / 2, Math.min(w - PIG_W / 2, px));
    pigXRef.current = px;
    if (pigRef.current) pigRef.current.style.transform = `translate3d(${px - PIG_W / 2}px, 0, 0)`;

    // Spawning. The normal spawner stands down for the whole crow wave (patrol AND flee —
    // the wave is the crow's stage), and permanently once scBoss silences the run.
    // Gated on `resolved`, NOT the visual phase: the crow can finish fleeing off-screen
    // (phase → "off") before his last drop has landed, and the random spawner must stay
    // silent until the wave has actually concluded (resolveCrowWave has run) — otherwise it
    // quietly resumes underneath a still-unresolved wave.
    if (crowRef.current.resolved && !silencedRef.current) {
      const params = stageParams(runRef.current.stage, mode);
      spawnAccRef.current += dt;
      if (spawnAccRef.current >= params.spawnMs) {
        spawnAccRef.current = 0;
        spawnObject();
      }
    }

    // The Greedy Crow. Patrol: position from the pure crowPosAt (deterministic in elapsed
    // wave-time), drops on a cadence with a squawk telegraph before each. Flee: defeated at
    // timer-up, he squawks off the nearest edge; the hat and the verdict wait for every
    // in-flight drop via `pending` (resolveCrowWave). All of it writes refs — the only
    // React state in the crow's whole life is the crowUi mount/unmount pair.
    const crow = crowRef.current;
    if (crow.phase === "patrol") {
      crow.elapsed += dt;
      const speed = crowPatrolSpeed(runRef.current.stage);
      const pos = crowPosAt(crow.elapsed, w, CROW_W, speed);
      const yBase = h * CROW_TOP_FRACTION;
      const crowY = yBase + pos.yOffset;
      if (crowElRef.current) crowElRef.current.style.transform = `translate3d(${pos.x - CROW_W / 2}px, ${crowY}px, 0)`;
      if (crowFlipRef.current) crowFlipRef.current.style.transform = pos.dir === 1 ? "scaleX(-1)" : "";
      const telegraphing = crow.telegraphAt != null;
      const showLayer = (key, on) => { const el = crowLayerRefs.current[key]; if (el) el.style.display = on ? "" : "none"; };
      const flapMs = telegraphing ? CROW_FLAP_MS : CROW_GLIDE_FLAP_MS;
      const frame = CROW_FLAP_FRAMES[Math.floor(crow.elapsed / flapMs) % CROW_FLAP_FRAMES.length];
      for (const fid of CROW_WING_IDS) { showLayer(`far-${fid}`, fid === frame); showLayer(`near-${fid}`, fid === frame); }
      showLayer("base-cw-base", !telegraphing);
      showLayer("base-cw-base-squawk", telegraphing);
      const sneaky = !telegraphing && crow.dropMs - crow.dropAcc < 350;
      const exprId = telegraphing ? "cx-furious" : sneaky ? "cx-sneaky" : "cx-mean";
      for (const eid of CROW_EXPR_IDS) showLayer(`expr-${eid}`, eid === exprId);
      // Drop cadence → telegraph → release from the claws at his live position.
      crow.dropAcc += dt;
      if (crow.telegraphAt == null && crow.dropAcc >= crow.dropMs) crow.telegraphAt = crow.elapsed;
      if (crow.telegraphAt != null && crow.elapsed - crow.telegraphAt >= CROW_TELEGRAPH_MS) {
        crow.telegraphAt = null;
        crow.dropAcc = 0;
        spawnCrowDrop(pos.x, crowY);
      }
      if (bossBarRef.current) bossBarRef.current.style.width = `${Math.min(100, (crow.elapsed / crow.waveMs) * 100)}%`;
      // Timer up: defeated. He flees off the nearest edge, furious; the hat drops at his
      // exit x once every in-flight drop resolves.
      if (crow.elapsed >= crow.waveMs) {
        crow.phase = "flee";
        crow.fleeX = pos.x;
        crow.fleeY = crowY;
        crow.fleeDir = pos.x < w / 2 ? -1 : 1;
        crow.hatX = pos.x;
        showLayer("base-cw-base", false);
        showLayer("base-cw-base-squawk", true);
        for (const eid of CROW_EXPR_IDS) showLayer(`expr-${eid}`, eid === "cx-furious");
        if (crow.pending === 0) resolveCrowWave();
      }
    } else if (crow.phase === "flee") {
      crow.elapsed += dt;
      crow.fleeX += crow.fleeDir * CROW_FLEE_SPEED * (dt / 1000);
      if (crowElRef.current) crowElRef.current.style.transform = `translate3d(${crow.fleeX - CROW_W / 2}px, ${crow.fleeY}px, 0)`;
      if (crowFlipRef.current) crowFlipRef.current.style.transform = crow.fleeDir === 1 ? "scaleX(-1)" : "";
      const showLayer = (key, on) => { const el = crowLayerRefs.current[key]; if (el) el.style.display = on ? "" : "none"; };
      const frame = CROW_FLAP_FRAMES[Math.floor(crow.elapsed / CROW_FLAP_MS) % CROW_FLAP_FRAMES.length];
      for (const fid of CROW_WING_IDS) { showLayer(`far-${fid}`, fid === frame); showLayer(`near-${fid}`, fid === frame); }
      if (crow.fleeX < -CROW_W || crow.fleeX > w + CROW_W) {
        crow.phase = "off";
        setCrowUi(false);
        if (scBoss && scBossWavesLeftRef.current > 0) { scBossWavesLeftRef.current -= 1; startCrowWave(); }
      }
    }

    // Advance objects + collisions. Catch zone = the pig's head/snout region, so catches
    // read as munches; Gentle gets a slightly wider hitbox. PIG_BOTTOM_GAP keeps this in
    // sync with the pig's actual (raised) resting position — see its definition above.
    const pigTop = h - PIG_H - PIG_BOTTOM_GAP;
    const halfHit = (PIG_W / 2) * (mode === "gentle" ? GENTLE_HITBOX_FACTOR : 0.95);
    for (const [id, o] of objDataRef.current) {
      o.y += o.speed * (dt / 1000);
      const el = objElsRef.current.get(id);
      if (el) el.style.transform = `translate3d(${o.x - OBJ_SIZE / 2}px, ${o.y}px, 0)`;
      const cy = o.y + OBJ_SIZE / 2;
      if (cy >= pigTop + 8 && cy <= pigTop + 74 && Math.abs(o.x - px) <= halfHit) {
        handleHit(id, o.kind);
        if (!runningRef.current) return;
      } else if (o.y > h) {
        handleFloor(id, o.kind);
        if (!runningRef.current) return;
      }
    }

    rafRef.current = requestAnimationFrame(step);
  }, [mode, spawnObject, spawnCrowDrop, resolveCrowWave, handleHit, handleFloor, scBoss, startCrowWave]);

  const startLoop = useCallback(() => {
    if (fieldRef.current) {
      const r = fieldRef.current.getBoundingClientRect();
      sizeRef.current = { w: r.width, h: r.height };
      if (pigXRef.current > r.width - PIG_W / 2) pigXRef.current = r.width / 2;
    }
    runningRef.current = true;
    lastTsRef.current = null;
    rafRef.current = requestAnimationFrame(step);
  }, [step]);

  // Boot the loop on mount; hard-stop on unmount.
  useEffect(() => {
    pigXRef.current = (fieldRef.current?.getBoundingClientRect().width || 360) / 2;
    startLoop();
    // scBoss: the run opens already inside the crow wave (short deterministic parameters).
    if (scBoss) startCrowWave();
    try { localStorage.setItem(HINT_KEY, "1"); } catch {}
    const onResize = () => {
      if (!fieldRef.current) return;
      const r = fieldRef.current.getBoundingClientRect();
      sizeRef.current = { w: r.width, h: r.height };
    };
    window.addEventListener("resize", onResize);
    return () => {
      runningRef.current = false;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (exprTimerRef.current) clearTimeout(exprTimerRef.current);
      if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
      window.removeEventListener("resize", onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-pause whenever the tab/app is backgrounded mid-run.
  useEffect(() => {
    function onVis() {
      if (document.visibilityState !== "visible" && runningRef.current) pause();
    }
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keyboard (desktop/Chromebook): held ← → at fixed px/s.
  useEffect(() => {
    function down(e) {
      if (e.key === "ArrowLeft") { keysRef.current.left = true; e.preventDefault(); }
      if (e.key === "ArrowRight") { keysRef.current.right = true; e.preventDefault(); }
    }
    function up(e) {
      if (e.key === "ArrowLeft") keysRef.current.left = false;
      if (e.key === "ArrowRight") keysRef.current.right = false;
    }
    window.addEventListener("keydown", down);
    window.addEventListener("keyup", up);
    return () => { window.removeEventListener("keydown", down); window.removeEventListener("keyup", up); };
  }, []);

  function pause() {
    runningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    setScreen("paused");
  }

  function resume() {
    setScreen("playing");
    startLoop();
  }

  function playAgain() {
    // Fresh run, warm-up already done today — reset everything and go straight back in.
    objDataRef.current = new Map();
    objElsRef.current = new Map();
    spawnAccRef.current = 0;
    spawnedCountRef.current = 0;
    crowRef.current = { phase: "off", elapsed: 0, dropAcc: 0, telegraphAt: null, pending: 0, resolved: true, heartsAtStart: STARTING_HEARTS, waveMs: 0, dropMs: 0, fleeX: 0, fleeDir: 1, hatX: null };
    silencedRef.current = false;
    setCrowUi(false);
    hatWornRef.current = true; // LB-329: a fresh round starts hatted (startCrowWave re-arms per wave too)
    setCrowHat(true);
    scBossWavesLeftRef.current = scBoss ? scBoss - 1 : 0;
    runRef.current = scBoss
      ? { ...initialRunState(), score: SC_BOSS_TEST.startScore, stage: stageForScore(SC_BOSS_TEST.startScore) }
      : { ...initialRunState(), hearts: scTest ? 1 : STARTING_HEARTS };
    setObjs([]);
    setFloats([]);
    setHud({ score: runRef.current.score, hearts: runRef.current.hearts, stage: runRef.current.stage, shielded: false });
    if (bannerTimerRef.current) clearTimeout(bannerTimerRef.current);
    setLevelBanner(null);
    setExpr("fx-happy");
    setGameOver(null);
    setScreen("playing");
    startLoop();
    if (scBoss) startCrowWave();
  }

  function onPointerDown(e) {
    if (screen !== "playing") return;
    const rect = fieldRef.current.getBoundingClientRect();
    pigTargetRef.current = e.clientX - rect.left;
    setShowHint(false);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }
  function onPointerMove(e) {
    if (screen !== "playing" || pigTargetRef.current == null) return;
    const rect = fieldRef.current.getBoundingClientRect();
    pigTargetRef.current = e.clientX - rect.left;
  }
  function onPointerUp() {
    pigTargetRef.current = null;
  }

  const hearts = Array.from({ length: scTest ? 1 : STARTING_HEARTS }, (_, i) => i < hud.hearts);

  return (
    <div className="w-full h-full relative select-none" style={{ touchAction: "none" }}>
      {/* One fixed soft sky-to-grass backdrop for all children (decision §0.4) with a couple
          of drifting decorative clouds. */}
      <div
        ref={fieldRef}
        className="absolute inset-0 overflow-hidden"
        style={{ background: "linear-gradient(180deg, #bfe3ff 0%, #dff2ff 55%, #b9e8a5 88%, #96d97e 100%)" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
      >
        <svg className="absolute opacity-80 sc-cloud" style={{ top: "12%", animationDuration: "46s" }} width="110" height="40" viewBox="0 0 110 40" aria-hidden="true">
          <ellipse cx="30" cy="26" rx="28" ry="13" fill="#ffffff" />
          <ellipse cx="62" cy="20" rx="30" ry="16" fill="#ffffff" />
          <ellipse cx="88" cy="27" rx="20" ry="11" fill="#ffffff" />
        </svg>
        <svg className="absolute opacity-70 sc-cloud" style={{ top: "27%", animationDuration: "64s", animationDelay: "-22s" }} width="90" height="34" viewBox="0 0 110 40" aria-hidden="true">
          <ellipse cx="30" cy="26" rx="28" ry="13" fill="#ffffff" />
          <ellipse cx="62" cy="20" rx="30" ry="16" fill="#ffffff" />
          <ellipse cx="88" cy="27" rx="20" ry="11" fill="#ffffff" />
        </svg>

        {/* The Greedy Crow (Boss v2, GREEDY_CROW_SCOPE.md): the sky tints, and he patrols on
            refs like every other actor. Layer order per the handoff: far wing at 0.85 →
            base → expression → near wing; frames and expressions are display-toggled in the
            rAF loop, never via React state. LB-329: the hat (cw-top-hat, z 40) is the last
            layer, inside the same flip group so it mirrors with him, drawn while crowHat is
            true (worn into every wave, lost on that wave's defeat drop). Rendered BEFORE the
            falling objects so his own drops pass in front of him. */}
        {crowUi && (
          <>
            <div data-testid="sc-crow-sky" className="absolute inset-0 bg-violet-700/15 pointer-events-none" />
            <div ref={crowElRef} data-testid="crow" className="absolute left-0 top-0 will-change-transform pointer-events-none" style={{ width: CROW_W, height: CROW_H, transform: `translate3d(-${CROW_W * 2}px, 0, 0)` }}>
              <div ref={crowFlipRef} style={{ width: "100%", height: "100%" }}>
                <svg viewBox="0 0 200 260" width={CROW_W} height={CROW_H} aria-hidden="true">
                  {CROW_WING_IDS.map((fid) => (
                    <use key={`far-${fid}`} href={`#${fid}`} opacity="0.85" style={{ display: fid === "cw-wing-mid" ? "" : "none" }} ref={(el) => { crowLayerRefs.current[`far-${fid}`] = el; }} />
                  ))}
                  <use href="#cw-base" ref={(el) => { crowLayerRefs.current["base-cw-base"] = el; }} />
                  <use href="#cw-base-squawk" style={{ display: "none" }} ref={(el) => { crowLayerRefs.current["base-cw-base-squawk"] = el; }} />
                  {CROW_EXPR_IDS.map((eid) => (
                    <use key={eid} href={`#${eid}`} style={{ display: eid === "cx-mean" ? "" : "none" }} ref={(el) => { crowLayerRefs.current[`expr-${eid}`] = el; }} />
                  ))}
                  {CROW_WING_IDS.map((fid) => (
                    <use key={`near-${fid}`} href={`#${fid}`} style={{ display: fid === "cw-wing-mid" ? "" : "none" }} ref={(el) => { crowLayerRefs.current[`near-${fid}`] = el; }} />
                  ))}
                  {crowHat && <use href="#cw-top-hat" data-testid="crow-hat" />}
                </svg>
              </div>
            </div>
          </>
        )}

        {/* Falling objects — membership in React, positions on refs. */}
        {objs.map((o) => (
          <div
            key={o.id}
            ref={(el) => { if (el) objElsRef.current.set(o.id, el); else objElsRef.current.delete(o.id); }}
            data-testid={`sc-obj-${o.kind}`}
            className="absolute left-0 top-0 will-change-transform"
            style={{ width: OBJ_SIZE, height: OBJ_SIZE, transform: "translate3d(-100px, -100px, 0)" }}
          >
            <svg width={OBJ_SIZE} height={OBJ_SIZE} aria-hidden="true"><use href={`#${OBJECT_KINDS[o.kind].symbol}`} /></svg>
          </div>
        ))}

        {/* Score floats at the catch point. */}
        {floats.map((f) => (
          <div key={f.id} className={`absolute font-extrabold text-sm sc-float pointer-events-none ${f.cls === "bad" ? "text-rose-600" : f.cls === "combo" ? "text-amber-500" : "text-emerald-600"}`} style={{ left: f.x - 30, top: f.y, width: 80, textAlign: "center" }}>
            {f.text}
          </div>
        ))}

        {/* Piggington — front-facing, the child's equipped cosmetics. Raised PIG_BOTTOM_GAP
            off the floor (not flush against it) so a dragging thumb has room to sit below
            him instead of on top of him — player feedback, Aug 2026. */}
        <div ref={pigRef} className="absolute left-0 will-change-transform" style={{ width: PIG_W, height: PIG_H, bottom: PIG_BOTTOM_GAP }}>
          <Piggington expression={expr} cosmetics={worn} base={characterBase} className="w-full h-full" />
        </div>

        {/* First-play-only drag hint, sitting in the strip below Piggington — exactly where
            a dragging thumb now naturally rests. */}
        {showHint && (
          <div className="absolute bottom-4 inset-x-0 flex items-center justify-center gap-2 text-slate-500/70 font-extrabold text-sm pointer-events-none">
            <span>←</span><span className="w-3 h-3 rounded-full bg-slate-500/50" /><span>→</span>
            <span className="ml-1">drag to move {pigName}!</span>
          </div>
        )}
      </div>

      {/* HUD on white pills so it survives the backdrop. */}
      <div className="absolute top-0 inset-x-0 p-3 flex items-center justify-between pointer-events-none" style={{ paddingTop: "calc(env(safe-area-inset-top, 0px) + 10px)" }}>
        <span data-testid="sc-score" className="bg-white/95 rounded-lg px-3.5 py-1.5 text-sm font-extrabold text-slate-800 shadow-sm">Score {hud.score}</span>
        <div className="flex items-center gap-1.5">
          <span data-testid="sc-hearts" className="bg-white/95 rounded-lg px-3 py-1.5 text-sm shadow-sm" aria-label={`${hud.hearts} hearts left`}>
            {hearts.map((full, i) => <span key={i}>{full ? "❤️" : "🤍"}</span>)}
          </span>
          {hud.shielded && (
            <span data-testid="sc-shield-badge" title="Tin hat — blocks the next hazard" className="bg-white/95 rounded-full w-8 h-8 flex items-center justify-center shadow-sm">
              <svg width="18" height="18" aria-hidden="true"><use href="#sa-tinhat" /></svg>
            </span>
          )}
        </div>
        <button aria-label="Pause" onClick={pause} className="bg-white/95 rounded-full w-9 h-9 text-slate-700 font-extrabold shadow-sm pointer-events-auto">⏸</button>
      </div>
      {levelBanner && (
        <div className="absolute top-16 inset-x-0 flex justify-center pointer-events-none" style={{ marginTop: "env(safe-area-inset-top, 0px)" }}>
          <span className="bg-slate-900/85 text-white text-xs font-extrabold rounded-lg px-3.5 py-1.5">{levelBanner}</span>
        </div>
      )}
      {/* Crow Wave countdown — a thin bar so the wave reads as a defined, beatable stretch
          rather than open-ended panic. Timer-driven now (the wave IS a timer, scope §0.5);
          the fill width is written per-frame on its ref by the rAF loop, same discipline as
          every other moving thing. */}
      {crowUi && (
        <div data-testid="sc-boss-bar" className="absolute top-24 inset-x-0 px-8 pointer-events-none" style={{ marginTop: "env(safe-area-inset-top, 0px)" }}>
          <div className="h-1.5 bg-white/40 rounded-full overflow-hidden">
            <div ref={bossBarRef} className="h-full bg-rose-500 rounded-full" style={{ width: "0%" }} />
          </div>
        </div>
      )}

      {/* Paused — also where an overlay-close attempt mid-run lands. Quit discards the run. */}
      {screen === "paused" && (
        <div data-testid="sc-paused" className="absolute inset-0 bg-black/60 flex items-center justify-center p-6">
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xs p-5 text-center">
            <div className="font-extrabold text-lg text-slate-900 mb-3">Paused</div>
            <button className="w-full bg-pink-500 text-white font-bold rounded-xl py-2.5 text-sm mb-2" onClick={resume}>Keep playing</button>
            <button data-testid="sc-quit" className="w-full bg-slate-100 text-slate-600 font-bold rounded-xl py-2.5 text-sm" onClick={onQuit}>Quit run</button>
          </div>
        </div>
      )}

      {/* Game over — card over the dimmed playfield. No balance, no currency, no "earn"
          language, ever. */}
      {screen === "gameover" && gameOver && (
        <div data-testid="sc-gameover" className="absolute inset-0 bg-black/60 flex items-center justify-center p-6">
          {gameOver.newBest && <Celebration variant="pig" count={20} />}
          <div className="bg-white rounded-3xl shadow-2xl w-full max-w-xs p-5 text-center">
            <div className="flex justify-center mb-1">
              <Piggington expression={gameOver.newBest ? "fx-celebrating" : "fx-happy"} cosmetics={worn} base={characterBase} className="w-16 h-[83px]" />
            </div>
            <div className="font-extrabold text-lg text-slate-900">{pigName}'s full!</div>
            <div className="text-xs text-slate-500 font-semibold mb-2">What a feast!</div>
            <div data-testid="sc-final-score" className="font-extrabold text-4xl text-slate-900 mb-2">{gameOver.score}</div>
            {gameOver.newBest ? (
              <span className="inline-block bg-amber-100 text-amber-700 font-extrabold text-xs rounded-lg px-3 py-1 mb-3">⭐ New best! ⭐</span>
            ) : (
              <span data-testid="sc-best" className="inline-block bg-slate-100 text-slate-500 font-extrabold text-xs rounded-lg px-3 py-1 mb-3">Best: {gameOver.best}</span>
            )}
            <button className="w-full bg-pink-500 text-white font-bold rounded-xl py-2.5 text-sm mb-2" onClick={playAgain}>Play again</button>
            <button className="w-full bg-slate-600 text-white font-bold rounded-xl py-2.5 text-sm" onClick={onQuit}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------ overlay shell ------------------------------ */

// Full-screen fixed inset-0 overlay (Oink Shop / VaultTransferOverlay pattern), locking
// scroll underneath. Opens on the Warm-Up screen if today's gate isn't done (and the parent
// hasn't switched this child to Free play), otherwise straight to the ready screen.
// LB-256: the arcade picker (ArcadePicker.jsx) now owns the warm-up and the arcade_opened
// event and mounts this with `embedded`; closing returns to the picker. Without `embedded`
// the overlay behaves exactly as it always has.
export function SnackCatchOverlay({ child, learning, scTest, scBoss, embedded, onClose }) {
  const needsWarmup = arcadeWarmupModeFor(learning) === "warmup" && !warmupDoneToday(child.id);
  const [screen, setScreen] = useState(needsWarmup ? "warmup" : "ready");
  // W2.5 feature adoption: one arcade_opened per overlay open (age band stamped by the
  // metrics module). Law 1 means the arcade never touches tokens, so this is the only
  // arcade event v1 needs — "are kids visiting" — without any reward coupling.
  useEffect(() => {
    if (!embedded) track("arcade_opened", {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [justUnlocked, setJustUnlocked] = useState(false);
  const pigName = pigDisplayNameFor(child);
  const best = readBest(child.id);

  return (
    <div data-testid="snack-catch-overlay" className="fixed inset-0 bg-white z-50 flex flex-col">
      <style>{`
        @keyframes sc-cloud-drift { 0% { transform: translateX(-140px); } 100% { transform: translateX(110vw); } }
        .sc-cloud { animation-name: sc-cloud-drift; animation-timing-function: linear; animation-iteration-count: infinite; }
        @keyframes sc-float-up { 0% { transform: translateY(0); opacity: 1; } 100% { transform: translateY(-42px); opacity: 0; } }
        .sc-float { animation: sc-float-up 0.85s ease-out forwards; }
        @keyframes sc-pop-out { 0% { transform: scale(1); opacity: 1; } 100% { transform: scale(1.45); opacity: 0; } }
        .sc-pop svg { animation: sc-pop-out 0.15s ease-out forwards; }
        @keyframes sc-pig-bounce { 0% { margin-bottom: 0; } 40% { margin-bottom: 10px; } 100% { margin-bottom: 0; } }
        .sc-bounce { animation: sc-pig-bounce 0.22s ease-out; }
        @keyframes sc-edge-flash { 0% { box-shadow: inset 0 0 0 10px rgba(244,63,94,0.55); } 100% { box-shadow: inset 0 0 0 0 rgba(244,63,94,0); } }
        .sc-flash { animation: sc-edge-flash 0.25s ease-out; }
        @keyframes sc-shield-edge-flash { 0% { box-shadow: inset 0 0 0 10px rgba(251,191,36,0.55); } 100% { box-shadow: inset 0 0 0 0 rgba(251,191,36,0); } }
        .sc-shield-flash { animation: sc-shield-edge-flash 0.25s ease-out; }
      `}</style>
      <SnackSprites />

      {screen === "warmup" && (
        <WarmupScreen
          child={child}
          learning={learning}
          scTest={scTest}
          onClose={onClose}
          onDone={() => { setJustUnlocked(true); setScreen("ready"); }}
        />
      )}

      {screen === "ready" && (
        <div data-testid="sc-ready" className="bg-white w-full h-full flex flex-col items-center justify-center px-6 py-6 relative overflow-y-auto">
          <button aria-label="Close" className="absolute top-4 right-4 text-slate-400 text-xl font-bold px-1.5" style={{ marginTop: "env(safe-area-inset-top, 0px)" }} onClick={onClose}>✕</button>
          {justUnlocked && <div className="font-extrabold text-pink-600 text-sm mb-3">Arcade open! Let's play!</div>}
          <Piggington expression="fx-happy" cosmetics={wornPiggingtonCosmetics(child)} base={equippedCharacterBaseId(child)} className="w-20 h-[104px] mb-2" />
          <div className="font-extrabold text-2xl text-slate-900 mb-1">Snack Attack</div>
          <p className="text-sm text-slate-500 font-semibold text-center max-w-xs mb-2">
            Help {pigName} catch the good stuff and dodge the bad! 3 lives — good luck!
          </p>
          <HowToPlayLegend />
          {best && <div className="text-xs font-bold text-slate-400 mb-3">Your best: {best.best}</div>}
          <button data-testid="sc-play" className="w-full max-w-xs bg-pink-500 text-white font-extrabold rounded-2xl py-3 text-base mt-2" onClick={() => setScreen("playing")}>▶ Play</button>
        </div>
      )}

      {screen === "playing" && (
        /* W1.14 (Aug 2026): clamp the playing area on tablets/wide screens. Full-bleed
           width made the field too wide to cover — objects could fall further sideways
           than a child can reasonably slide in time. MAX_PLAYFIELD_W leaves phones
           untouched (they're narrower than the clamp) and gives tablets a phone-like
           column with a dark arcade surround. All game physics (sizeRef, pointer x)
           already read the field's own bounding rect on mount/resize, so the loop adapts
           with no other change. Chosen over a portrait orientation lock because the
           Screen Orientation API can't be relied on outside fullscreen in a PWA. */
        <div className="w-full h-full flex justify-center" style={{ background: "#0f172a" }}>
          <div data-testid="sc-playfield-clamp" className="w-full h-full relative" style={{ maxWidth: MAX_PLAYFIELD_W }}>
            <GameScreen child={child} learning={learning} scTest={scTest} scBoss={scBoss} onQuit={onClose} />
          </div>
        </div>
      )}
    </div>
  );
}
